<template>
	<nav
		ref="container"
		class="breadcrumb"
		:aria-label="$t('breadcrumb.aria-label')"
	>
		<ol class="breadcrumb-list">
			<li
				v-for="(inlineBreadcrumb, inlineIndex) in inlineBreadcrumbs"
				:key="inlineIndex"
				ref="breadcrumb"
				:class="`breadcrumb-item breadcrumb-item--${inlineBreadcrumb.page}-page`"
			>
				<!-- Separator -->
				<span
					v-if="showSeparator(inlineBreadcrumb)"
					aria-hidden="true"
					class="breadcrumb-item__separator"
				>
					{{ separator ? separator : '›' }}
				</span>

				<!-- Home page inline breadcrumb options -->
				<helio-link
					v-if="homeIcon && inlineBreadcrumb.page === 'home'"
					:aria-label="$t('breadcrumb.home')"
					:link="inlineBreadcrumb.url"
					class="breadcrumb-item__link"
				>
					<helio-icon
						class="breadcrumb-item__icon"
						token="home"
						:weight="getWeight(inlineBreadcrumb)"
					/>
				</helio-link>

				<helio-link
					v-if="!homeIcon && inlineBreadcrumb.page === 'home'"
					:aria-label="$t('breadcrumb.home')"
					:link="inlineBreadcrumb.url"
					class="breadcrumb-item__link"
				>
					<helio-icon
						v-if="getToken(inlineBreadcrumb).length > 0"
						aria-hidden="true"
						class="breadcrumb-item__icon"
						:token="getToken(inlineBreadcrumb)"
						:weight="getWeight(inlineBreadcrumb)"
					/>

					{{ inlineBreadcrumb.title }}
				</helio-link>

				<!-- Regular inline breadcrumb -->
				<helio-link
					v-if="inlineBreadcrumb.page === ''"
					:link="inlineBreadcrumb.url"
					class="breadcrumb-item__link"
				>
					<helio-icon
						v-if="getToken(inlineBreadcrumb).length > 0"
						aria-hidden="true"
						class="breadcrumb-item__icon"
						:token="getToken(inlineBreadcrumb)"
						:weight="getWeight(inlineBreadcrumb)"
					/>

					{{ inlineBreadcrumb.title }}
				</helio-link>

				<!-- Current page inline breadcrumb -->
				<a
					v-if="includeCurrentPage && inlineBreadcrumb.page === 'current'"
					href="#"
					aria-current="location"
					class="breadcrumb-item__link breadcrumb-item__link--current-page"
				>
					<helio-icon
						v-if="getToken(inlineBreadcrumb).length > 0"
						aria-hidden="true"
						class="breadcrumb-item__icon"
						:token="getToken(inlineBreadcrumb)"
						:weight="getWeight(inlineBreadcrumb)"
					/>

					{{ inlineBreadcrumb.title }}
				</a>

				<!-- Inline trigger for dropdown menu -->
				<div
					v-if="inlineBreadcrumb.page === 'menu'"
					:aria-expanded="isMenuOpen"
					:aria-label="$t('breadcrumb.menu')"
					role="menu"
					class="breadcrumb-item__link breadcrumb-item__menu-trigger"
					:tabindex="dropdownBreadcrumbs.length > 0 ? 0 : -1"
					@click="toggleMenuState"
					@keyup.enter="toggleMenuState"
					@keyup.space="toggleMenuState"
				>
					<div
						v-if="dropdownBreadcrumbs.length > 0"
						:data-anchor="`menu-trigger-${_uid}`"
						class="breadcrumb-item__menu-popup-anchor"
					/>

					<span aria-hidden="true">⋯</span>

					<!-- Dropdown menu -->
					<div v-if="hasMenu">
						<div class="breadcrumb-item__menu-popup-wrapper">
							<helio-popup
								ref="menu"
								:active="isMenuOpen"
								:anchor="`menu-trigger-${_uid}`"
								:custom-classes="menuClasses"
								:gap="menuGap"
								:close-on-outside-click="true"
								:mobile-overlay="false"
								offset="right"
								:parent-position="parentPosition"
								position="bottom"
								@close="closeMenu"
							>
								<template v-slot:content>
									<ol class="breadcrumb-item__menu-list">
										<li
											v-for="(dropdownBreadcrumb, dropdownIndex) in dropdownBreadcrumbs"
											:key="dropdownIndex"
											class="breadcrumb-item__menu-list-item"
										>
											<helio-link
												v-if="dropdownBreadcrumb.page !== 'current'"
												:link="dropdownBreadcrumb.url"
												class="breadcrumb-item__menu-list-item-link"
												@click.native="closeMenu"
											>
												{{ dropdownBreadcrumb.title }}
											</helio-link>
										</li>
									</ol>
								</template>
							</helio-popup>
						</div>
					</div>
				</div>
			</li>
		</ol>
	</nav>
</template>

<script>
import Icon from '../icon/Icon.vue';
import Link from '../link/Link.vue';
import Popup from '../popup/Popup.vue';

export default {
	name: 'Breadcrumb',

	components: {
		'helio-icon': Icon,
		'helio-link': Link,
		'helio-popup': Popup,
	},

	props: {
		breadcrumbData: {
			type: Array,
			required: true,
			description: 'Supplies the data required to populate the breadcrumb',
		},

		fallbackIconToken: {
			type: String,
			required: false,
			default: '',
			description: 'Sets whether an icon will appear beside each breadcrumb',
		},

		homeIcon: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a home icon will be used in place of text for the top-level breadcrumb',
		},

		includeCurrentPage: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the breadcrumb for the current page will be displayed',
		},

		menuGap: {
			type: Number,
			required: false,
			default: 25,
			description: 'Sets the distance between the menu and the menu trigger',
		},

		menuTheme: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'light',
				'dark',
			].indexOf(value) !== -1,
			description: 'Sets whether the dropdown menu will have a dark or light theme',
		},

		parentPosition: {
			type: String,
			required: false,
			default: 'normal',
			description: 'Provides context about the parent\'s position. This context determines how the Pop component will position the Popup.',
		},

		separator: {
			type: String,
			required: false,
			default: '›',
			description: 'Sets whether a default or custom separator will be used between each breadcrumb',
		},
	},

	data() {
		return {
			inlineBreadcrumbs: [],
			dropdownBreadcrumbs: [],
			isMenuOpen: false,
			inlineBreadcrumbsWidth: null,
			longestBreadcrumbWidth: null,
			hasDarkMenu: false,
			menuTrigger: { page: 'menu' },
		};
	},

	computed: {
		hasMenu() {
			return this.dropdownBreadcrumbs.length > 0;
		},

		menuClasses() {
			let classString = 'breadcrumb-item__menu-popup';
			classString += ` ${this.menuTheme}-theme`;
			return classString;
		},
	},

	watch: {
		breadcrumbData(newValue) {
			const newBreadcrumbData = this.cloneObject(newValue);
			this.setBreadcrumbs(newBreadcrumbData);
		},

		includeCurrentPage() {
			const newBreadcrumbData = this.cloneObject(this.breadcrumbData);
			this.setBreadcrumbs(newBreadcrumbData);
		},
	},

	created() {
		this.isMenuOpen = false;
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);
		window.addEventListener('resize', this.onResize);
		const currentBreadcrumbData = this.cloneObject(this.breadcrumbData);
		this.setBreadcrumbs(currentBreadcrumbData);
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.formatBreadcrumbs();
		},

		setBreadcrumbs(breadcrumbsData) {
			// remove current page from the data if includeCurrentPage prop is false
			const breadcrumbs = breadcrumbsData;
			const breadcrumbsWithoutCurrentPage = breadcrumbsData.filter(breadcrumb => breadcrumb.page !== 'current');

			// set dropdown and inline crumb arrays

			this.dropdownBreadcrumbs = [];
			this.inlineBreadcrumbs = this.includeCurrentPage
				? breadcrumbs
				: breadcrumbsWithoutCurrentPage;

			// format and collapse breadcrumbs as needed
			this.formatBreadcrumbs();
			this.collapseBreadcrumbs();
		},

		formatBreadcrumbs() {
			this.$nextTick(() => {
				// collect width of crumbs
				const breadcrumbs = this.$refs.breadcrumb;
				if (breadcrumbs) {
					// get widths of each crumb
					const crumbWidths = breadcrumbs.map(item => item.clientWidth);
					// get width of longest crumb for use in expandBreadcrumbs() method
					this.longestBreadcrumbWidth = crumbWidths
						.reduce((previousValue, currentValue) => Math.max(previousValue, currentValue));
					// get total width of inline crumbs for use in expandBreadcrumbs() method
					const inlineWidths = breadcrumbs.map(item => item.clientWidth);
					this.inlineBreadcrumbsWidth = inlineWidths
						.reduce((previousValue, currentValue) => previousValue + currentValue);
				}

				//  establish whether container width is growing or shrinking
				const { container } = this.$refs;
				let oldWidth = null;
				let newWidth = null;

				oldWidth = Number(container.clientWidth);

				setTimeout(() => {
					newWidth = Number(container.clientWidth);

					if (oldWidth >= newWidth) {
						this.collapseBreadcrumbs();
					} else {
						this.expandBreadcrumbs();
					}
				}, 30);
			});
		},

		collapseBreadcrumbs() {
			this.$nextTick(() => {
				const { container } = this.$refs;
				const availableSpaceInContainer = container.clientWidth - 10;
				const inlineCrumbsNotCountingTrigger = this.inlineBreadcrumbs.filter(crumb => crumb.page !== 'menu');
				const dropdownMenuEmpty = this.dropdownBreadcrumbs.length === 0;

				if (this.inlineBreadcrumbsWidth > availableSpaceInContainer) {
					if (inlineCrumbsNotCountingTrigger.length >= 4) {
						if (dropdownMenuEmpty) {
							/* if dropdown menu is empty and we have at least 4 crumbs,
							we move 2 crumbs into the menu to start */
							const removedBreadcrumbs = this.inlineBreadcrumbs.splice(1, 2, this.menuTrigger);
							this.dropdownBreadcrumbs = this.dropdownBreadcrumbs.concat(removedBreadcrumbs);
						} else if (!dropdownMenuEmpty) {
							/* if dropdown menu is not empty and we have at least 4 crumbs,
							we move 1 crumb into the menu at a time */
							const removedBreadcrumbs = this.inlineBreadcrumbs.splice(2, 1);
							this.dropdownBreadcrumbs = this.dropdownBreadcrumbs.concat(removedBreadcrumbs);
						}
					} else if (inlineCrumbsNotCountingTrigger.length === 3) {
						if (dropdownMenuEmpty) {
							/* if dropdown menu is empty and we have exactly 3 crumbs,
							we move the first 2 crumbs into the menu */
							const removedBreadcrumbs = this.inlineBreadcrumbs.splice(0, 2, this.menuTrigger);
							this.dropdownBreadcrumbs = this.dropdownBreadcrumbs.concat(removedBreadcrumbs);
						}
						if (!dropdownMenuEmpty) {
							/* if dropdown menu is not empty and we have exactly 3 crumbs,
							we move the 1 crumb into the menu */
							const removedBreadcrumbs = this.inlineBreadcrumbs.splice(2, 1);
							this.dropdownBreadcrumbs = this.dropdownBreadcrumbs.concat(removedBreadcrumbs);
						}
					} else if (inlineCrumbsNotCountingTrigger.length === 2) {
						if (!dropdownMenuEmpty) {
							/* if dropdown menu is not empty and we have exactly 2 crumbs,
							we move the home page crumb into the menu */
							const removedBreadcrumbs = this.inlineBreadcrumbs.splice(0, 1);
							this.dropdownBreadcrumbs = removedBreadcrumbs.concat(this.dropdownBreadcrumbs);
						}
					}

					/* here we loop onResize() until, one by one,
					each of the crumbs that surpasses the width of the window
					has been placed in the menu */
					if (inlineCrumbsNotCountingTrigger.length >= 2) {
						this.onResize();
					}
				}
			});
		},

		expandBreadcrumbs() {
			this.$nextTick(() => {
				const { container } = this.$refs;
				const containerWidth = container.clientWidth;

				if (
					this.dropdownBreadcrumbs.length > 2
					&& containerWidth > this.inlineBreadcrumbsWidth + this.longestBreadcrumbWidth + 40
				) {
					if (this.inlineBreadcrumbs.length === 2) {
						/* if inline has the menu trigger and exactly 1 crumb,
						we remove the home page crumb from the dropdown menu */
						const removedBreadcrumbs = this.dropdownBreadcrumbs.splice(0, 1);
						this.inlineBreadcrumbs = removedBreadcrumbs.concat(this.inlineBreadcrumbs);
					} else if (this.inlineBreadcrumbs.length >= 3) {
						/* if inline has the menu trigger and at least 2 crumbs,
						and if the dropdown menu has at least 3 crumbs,
						we remove 1 crumb from the menu at a time */
						const removedBreadcrumbs = this.dropdownBreadcrumbs
							.splice(this.dropdownBreadcrumbs.length - 1, 1);
						this.inlineBreadcrumbs.splice(2, 0, removedBreadcrumbs[0]);
					}
				} else if (
					this.dropdownBreadcrumbs.length === 2
					&& containerWidth > this.inlineBreadcrumbsWidth + ((this.longestBreadcrumbWidth + 40) * 2)
				) {
					if (this.inlineBreadcrumbs.length === 2) {
						/* if inline has the menu trigger and exactly 1 crumb,
						and if the dropdown has only 2 crumbs left,
						we remove both crumbs from the dropdown,
						and we remove the menu trigger */
						const removedBreadcrumbs = this.dropdownBreadcrumbs.splice(0, 2);
						this.inlineBreadcrumbs.splice(0, 1, removedBreadcrumbs[0], removedBreadcrumbs[1]);
					} else if (this.inlineBreadcrumbs.length >= 3) {
						/* if inline has the menu trigger and at least 2 crumbs,
						and if the dropdown has only 2 crumbs left,
						we remove both crumbs from the dropdown,
						and we remove the menu trigger */
						const removedBreadcrumbs = this.dropdownBreadcrumbs.splice(0, 2);
						this.inlineBreadcrumbs.splice(2, 0, removedBreadcrumbs[0], removedBreadcrumbs[1]);
						this.inlineBreadcrumbs.splice(1, 1);
					}
				}
			});
		},

		showSeparator(inlineBreadcrumb) {
			return inlineBreadcrumb.page !== 'home'
				&& (inlineBreadcrumb.page !== 'menu' || this.inlineBreadcrumbs[0].page !== 'menu')
				&& (inlineBreadcrumb.page !== 'current' || this.includeCurrentPage);
		},

		getToken(breadcrumb) {
			const token = (breadcrumb.icon && breadcrumb.icon.token)
				? breadcrumb.icon.token
				: this.fallbackIconToken;
			return token;
		},

		getWeight(breadcrumb) {
			return (breadcrumb.icon && breadcrumb.icon.weight)
				? breadcrumb.icon.weight
				: 'light';
		},

		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27 && this.isMenuOpen) {
				this.closeMenu();
			}
		},

		closeMenu() {
			if (this.isMenuOpen) {
				this.isMenuOpen = false;
				this.$emit('close menu');
			}
		},

		openMenu() {
			if (!this.isMenuOpen) {
				this.isMenuOpen = true;
				this.$emit('open menu');
			}
		},

		toggleMenuState() {
			if (!this.isMenuOpen) {
				this.openMenu();
			} else {
				this.closeMenu();
			}
		},
	},
};
</script>

<style lang="scss" src="./breadcrumb.scss" />
